import { geoDistance, geoOrthographic, geoPath } from 'd3-geo';
import type { GeoPermissibleObjects } from 'd3-geo';
import { useEffect, useState } from 'preact/hooks';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import landUrl from 'world-atlas/land-110m.json?url';

/**
 * The spinning globe from the design — behind every card on the tracking
 * screen, and under the flying postcard once one is sent.
 *
 * Every globe on screen shares one rotation and one projection, advanced on the
 * design's cadence (1.1° every 110ms), so several cards cost one path per tick.
 * Geometry is drawn in a 100×100 viewBox, matching the design's SVG.
 */

// Approximate [lon, lat] per country, from the design.
export const COORD: Record<string, [number, number]> = {
  ecuador: [-78.5, -0.2], colombia: [-74.1, 4.7], 'perú': [-77, -12], 'méxico': [-99.1, 19.4],
  argentina: [-58.4, -34.6], chile: [-70.6, -33.4], 'españa': [-3.7, 40.4],
  'estados unidos': [-77, 38.9], 'canadá': [-79.4, 43.7], 'reino unido': [-0.1, 51.5],
  alemania: [13.4, 52.5], francia: [2.3, 48.9], italia: [12.5, 41.9], portugal: [-9.1, 38.7],
  brasil: [-46.6, -23.5], 'países bajos': [4.9, 52.4],
};

const TICK_MS = 110;
const STEP_DEG = 1.1;
const TILT = -12;

const projection = geoOrthographic().fitSize([94, 94], { type: 'Sphere' });
{
  const [tx, ty] = projection.translate();
  projection.translate([tx + 3, ty + 3]);
}
const path = geoPath(projection);

let land: GeoPermissibleObjects | null = null;
let loading: Promise<void> | null = null;
let lambda = 70;
let cachedFor = Number.NaN;
let cachedLand = '';
let timer: number | undefined;
const subscribers = new Set<() => void>();

function notify(): void {
  for (const fn of subscribers) fn();
}

function loadLand(): void {
  loading ??= fetch(landUrl)
    .then((r) => r.json() as Promise<Topology>)
    .then((topo) => {
      land = feature(topo, topo.objects['land']!) as GeoPermissibleObjects;
      cachedFor = Number.NaN;
      notify();
    })
    .catch(() => {
      // Without the map the globe still draws its rings; try again next mount.
      loading = null;
    });
}

const reducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function start(): void {
  if (timer !== undefined || reducedMotion()) return;
  timer = window.setInterval(() => {
    if (document.hidden) return;
    lambda = (lambda + STEP_DEG) % 360;
    notify();
  }, TICK_MS);
}

function stopIfIdle(): void {
  if (timer !== undefined && subscribers.size === 0) {
    window.clearInterval(timer);
    timer = undefined;
  }
}

export interface GlobeFrame {
  /** SVG path data for the land masses; null until the map has loaded. */
  land: string | null;
  /** Where a [lon, lat] point sits on the globe, and whether it faces the viewer. */
  project(lonLat: [number, number]): { x: number; y: number; visible: boolean } | null;
}

function frame(): GlobeFrame {
  projection.rotate([lambda, TILT]);
  if (land && cachedFor !== lambda) {
    cachedLand = path(land) ?? '';
    cachedFor = lambda;
  }
  const centre: [number, number] = [-lambda, -TILT];
  return {
    land: land ? cachedLand : null,
    project(lonLat) {
      projection.rotate([lambda, TILT]);
      const p = projection(lonLat);
      if (!p) return null;
      return { x: p[0], y: p[1], visible: geoDistance(lonLat, centre) < Math.PI / 2 };
    },
  };
}

/** Subscribes a component to the shared rotation; it re-renders on every tick. */
export function useGlobe(): GlobeFrame {
  const [, setTick] = useState(0);
  useEffect(() => {
    const rerender = () => setTick((n) => n + 1);
    subscribers.add(rerender);
    loadLand();
    start();
    return () => {
      subscribers.delete(rerender);
      stopIfIdle();
    };
  }, []);
  return frame();
}
